import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ShieldCheckIcon, 
  LockClosedIcon, 
  DocumentTextIcon,
  UserIcon,
  EyeIcon,
  ServerIcon,
  GlobeAltIcon,
  FingerPrintIcon,
  EnvelopeIcon,
  CogIcon
} from '@heroicons/react/24/outline';

const Privacy = () => {
  // Animation variants
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  const staggerItems = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
      {/* Hero Section */}
      <motion.div 
        className="relative bg-gradient-to-br from-indigo-600 via-purple-600 to-blue-700 dark:from-indigo-900 dark:via-purple-900 dark:to-blue-900 py-16 overflow-hidden"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        {/* Background pattern and details */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full bg-gradient-to-br from-indigo-400 to-blue-400 opacity-20 blur-3xl"></div>
          <div className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full bg-gradient-to-tr from-purple-400 to-indigo-400 opacity-20 blur-3xl"></div>
          <div className="absolute inset-0 opacity-10 bg-grid-pattern"></div>
        </div>
        
        <div className="max-w-5xl mx-auto px-4 relative z-10 text-center">
          <motion.h1 
            className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-white to-blue-100">
              Gizlilik Politikası
            </span>
          </motion.h1>
          
          <motion.p 
            className="text-blue-100 max-w-3xl mx-auto text-lg"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
          >
            Dersmaster olarak kişisel verilerinizin güvenliğini ve gizliliğini önemsiyoruz
          </motion.p>
        </div>
      </motion.div>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-4 py-12">
        {/* Last Updated Section */}
        <motion.div 
          className="mb-12 text-sm text-right text-gray-500 dark:text-gray-400"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
        >
          Son Güncelleme: 01 Mayıs 2025
        </motion.div>

        {/* Table of Contents */}
        <motion.section
          variants={fadeIn}
          initial="hidden"
          animate="visible"
          className="mb-12 bg-white dark:bg-gray-800 rounded-xl shadow-md p-6"
        >
          <h2 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">İçindekiler</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              { id: "intro", title: "Giriş", icon: <DocumentTextIcon className="h-5 w-5" /> },
              { id: "data-collection", title: "Veri Toplama", icon: <UserIcon className="h-5 w-5" /> },
              { id: "data-use", title: "Veri Kullanımı", icon: <EyeIcon className="h-5 w-5" /> },
              { id: "data-sharing", title: "Veri Paylaşımı", icon: <ServerIcon className="h-5 w-5" /> },
              { id: "security", title: "Veri Güvenliği", icon: <ShieldCheckIcon className="h-5 w-5" /> },
              { id: "user-rights", title: "Kullanıcı Hakları", icon: <LockClosedIcon className="h-5 w-5" /> },
              { id: "cookies", title: "Çerezler ve İzleme", icon: <FingerPrintIcon className="h-5 w-5" /> },
              { id: "updates", title: "Politika Güncellemeleri", icon: <CogIcon className="h-5 w-5" /> },
              { id: "contact", title: "İletişim", icon: <EnvelopeIcon className="h-5 w-5" /> },
            ].map((item) => (
              <a 
                key={item.id} 
                href={`#${item.id}`} 
                className="flex items-center p-3 rounded-lg hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors group"
              >
                <span className="p-2 rounded-full bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 mr-3">
                  {item.icon}
                </span>
                <span className="text-gray-700 dark:text-gray-200 group-hover:text-indigo-600 dark:group-hover:text-indigo-400">
                  {item.title}
                </span>
              </a>
            ))}
          </div>
        </motion.section>

        {/* Privacy Policy Content */}
        <motion.div 
          variants={staggerItems} 
          initial="hidden" 
          animate="visible"
          className="space-y-10"
        >
          {/* Giriş */}
          <motion.section 
            id="intro" 
            variants={fadeIn} 
            className="prose prose-lg max-w-none dark:prose-invert prose-headings:text-gray-800 dark:prose-headings:text-white prose-p:text-gray-600 dark:prose-p:text-gray-300 prose-a:text-indigo-600 dark:prose-a:text-indigo-400 prose-a:no-underline hover:prose-a:underline"
          >
            <h2 className="flex items-center">
              <DocumentTextIcon className="h-8 w-8 text-indigo-600 dark:text-indigo-500 mr-3" />
              1. Giriş
            </h2>
            
            <p>
              Bu Gizlilik Politikası, Dersmaster platformunu ("Platform") kullanımınız sırasında topladığımız kişisel verilerinizin nasıl işlendiğini açıklamaktadır. Dersmaster Teknoloji A.Ş. ("Dersmaster", "biz", "bizim" veya "bize") olarak, kişisel verilerinizin gizliliğini korumayı ve 6698 sayılı Kişisel Verilerin Korunması Kanunu ("KVKK") başta olmak üzere tüm ilgili yasal düzenlemelere uygun şekilde işlemeyi taahhüt ediyoruz.
            </p>
            
            <p>
              Bu politika, Platformumuzu kullanmanız durumunda kişisel verilerinizin toplanması, kullanılması, paylaşılması ve korunmasına ilişkin politika ve prosedürlerimizi belirtmektedir. Platformumuzu kullanarak bu Gizlilik Politikası'nda belirtilen uygulamaları kabul etmiş sayılırsınız.
            </p>
            
            <div className="bg-indigo-50 dark:bg-indigo-900/20 border-l-4 border-indigo-400 dark:border-indigo-600 p-4 mt-4">
              <p className="text-sm text-indigo-700 dark:text-indigo-300">
                <strong>Not:</strong> Bu Gizlilik Politikası, Dersmaster platformunun kullanımı ile ilgilidir ve diğer web sitelerine olan bağlantıları kapsamaz. Diğer sitelere yönlendirildiğinizde, lütfen bu sitelerin kendi gizlilik politikalarını inceleyiniz.
              </p>
            </div>
          </motion.section>
          
          {/* Veri Toplama */}
          <motion.section 
            id="data-collection" 
            variants={fadeIn}
            className="prose prose-lg max-w-none dark:prose-invert prose-headings:text-gray-800 dark:prose-headings:text-white prose-p:text-gray-600 dark:prose-p:text-gray-300"
          >
            <h2 className="flex items-center">
              <UserIcon className="h-8 w-8 text-indigo-600 dark:text-indigo-500 mr-3" />
              2. Topladığımız Veriler
            </h2>
            
            <p>
              Platform üzerinde hizmet sağlamak ve kullanıcı deneyimini geliştirmek amacıyla aşağıdaki kişisel verileri toplayabiliriz:
            </p>
            
            <h3>2.1 Hesap Bilgileri</h3>
            <ul>
              <li>İsim, soyisim</li>
              <li>E-posta adresi</li>
              <li>Telefon numarası</li>
              <li>Şifre (şifrelenmiş formatta)</li>
              <li>Profil fotoğrafı (isteğe bağlı)</li>
              <li>Doğum tarihi</li>
            </ul>
            
            <h3>2.2 Eğitmen Bilgileri (Eğitmenler için)</h3>
            <ul>
              <li>Eğitim geçmişi</li>
              <li>Mesleki sertifikalar</li>
              <li>İş deneyimi</li>
              <li>Uzmanlık alanları</li>
              <li>Özgeçmiş bilgileri</li>
              <li>Banka hesap bilgileri (ödeme için)</li>
            </ul>
            
            <h3>2.3 Konum Bilgileri</h3>
            <ul>
              <li>Şehir</li>
              <li>İlçe</li>
              <li>Eğitmenler için: Ders vermek istediğiniz bölgeler</li>
              <li>Öğrenciler için: Ders almak istediğiniz bölgeler</li>
            </ul>
            
            <h3>2.4 Otomatik Olarak Toplanan Bilgiler</h3>
            <ul>
              <li>IP adresi</li>
              <li>Cihaz bilgisi</li>
              <li>Tarayıcı türü ve dil ayarları</li>
              <li>Kullanım süresi ve ziyaret edilen sayfalar</li>
              <li>Platform içi aktiviteler</li>
              <li>Çerezler aracılığıyla toplanan bilgiler</li>
            </ul>
            
            <div className="bg-purple-50 dark:bg-purple-900/20 border-l-4 border-purple-400 dark:border-purple-600 p-4 mt-4">
              <p className="text-sm text-purple-700 dark:text-purple-300">
                <strong>Önemli:</strong> Platformumuzu ziyaret ettiğinizde teknik bilgileriniz otomatik olarak toplanır, ancak bu bilgiler KVKK kapsamında kimliğinizi belirlemek için tek başına yeterli değildir. Bu bilgiler genellikle Platform'u geliştirmek ve kullanıcı deneyimini artırmak amacıyla kullanılır.
              </p>
            </div>
          </motion.section>
          
          {/* Veri Kullanımı */}
          <motion.section 
            id="data-use" 
            variants={fadeIn}
            className="prose prose-lg max-w-none dark:prose-invert prose-headings:text-gray-800 dark:prose-headings:text-white prose-p:text-gray-600 dark:prose-p:text-gray-300"
          >
            <h2 className="flex items-center">
              <EyeIcon className="h-8 w-8 text-indigo-600 dark:text-indigo-500 mr-3" />
              3. Veri Kullanımı
            </h2>
            
            <p>
              Topladığımız kişisel verilerinizi aşağıdaki amaçlar doğrultusunda kullanıyoruz:
            </p>
            
            <h3>3.1 Hizmet Sağlama</h3>
            <ul>
              <li>Kullanıcı hesabı oluşturma ve yönetme</li>
              <li>Öğrenciler ve eğitmenler arasında ders eşleştirmesi yapma</li>
              <li>Ders rezervasyonu ve planlaması</li>
              <li>Ödeme işlemlerini gerçekleştirme</li>
              <li>Kullanıcı desteği sağlama</li>
            </ul>
            
            <h3>3.2 Hizmet İyileştirme</h3>
            <ul>
              <li>Kullanıcı deneyimini geliştirme</li>
              <li>Platform özelliklerini ve işlevselliğini iyileştirme</li>
              <li>İstatistiksel analizler yapma</li>
              <li>Ürün geliştirme ve araştırma</li>
            </ul>
            
            <h3>3.3 İletişim</h3>
            <ul>
              <li>Hizmet güncellemeleri hakkında bilgilendirme</li>
              <li>Platform özellikleriyle ilgili duyurular</li>
              <li>Kişiselleştirilmiş içerik ve öneriler</li>
              <li>Destek talepleri ve sorunlar hakkında iletişim</li>
              <li>Kullanıcı araştırma ve anketleri</li>
            </ul>
            
            <h3>3.4 Güvenlik ve Doğrulama</h3>
            <ul>
              <li>Hesap güvenliğini sağlama</li>
              <li>Dolandırıcılık ve kötüye kullanımı önleme</li>
              <li>Platformun güvenliğini sağlama</li>
              <li>Kimlik doğrulama</li>
            </ul>
            
            <div className="bg-green-50 dark:bg-green-900/20 border-l-4 border-green-400 dark:border-green-600 p-4">
              <p className="text-sm text-green-700 dark:text-green-300">
                <strong>Yasal Dayanak:</strong> Kişisel verileriniz, KVKK'nın 5. ve 6. maddelerinde belirtilen şartlar çerçevesinde işlenmektedir. Bunlar arasında sözleşme ilişkisi, kanuni yükümlülükler, meşru menfaatler ve açık rızanız yer almaktadır.
              </p>
            </div>
          </motion.section>
          
          {/* Veri Paylaşımı */}
          <motion.section 
            id="data-sharing" 
            variants={fadeIn}
            className="prose prose-lg max-w-none dark:prose-invert prose-headings:text-gray-800 dark:prose-headings:text-white prose-p:text-gray-600 dark:prose-p:text-gray-300"
          >
            <h2 className="flex items-center">
              <ServerIcon className="h-8 w-8 text-indigo-600 dark:text-indigo-500 mr-3" />
              4. Veri Paylaşımı
            </h2>
            
            <p>
              Kişisel verilerinizi üçüncü taraflarla aşağıdaki durumlarda paylaşabiliriz:
            </p>
            
            <h3>4.1 Platform İçi Paylaşım</h3>
            <p>
              Platformun doğası gereği, öğrenciler ve eğitmenler arasında ders eşleştirmesi yapabilmek amacıyla belirli bilgileri platformdaki diğer kullanıcılarla paylaşırız:
            </p>
            <ul>
              <li>Eğitmenler için: İsim, profil resmi, uzmanlık alanları, deneyim bilgileri, eğitim geçmişi ve genel konum bilgisi</li>
              <li>Öğrenciler için: İsim, profil resmi ve ders talepleri</li>
            </ul>
            
            <h3>4.2 Hizmet Sağlayıcılar</h3>
            <p>
              Platform hizmetlerini sağlamak için çeşitli üçüncü taraf hizmet sağlayıcılarla çalışıyoruz:
            </p>
            <ul>
              <li>Ödeme işlemcileri</li>
              <li>Hosting ve bulut depolama sağlayıcıları</li>
              <li>İletişim ve bildirim hizmetleri</li>
              <li>Veri analizi sağlayıcıları</li>
              <li>Kullanıcı desteği sağlayıcıları</li>
            </ul>
            <p>
              Bu hizmet sağlayıcılarla yapılan sözleşmeler, kişisel verilerinizin güvenliği ve gizliliğini koruma yükümlülüklerini içermektedir.
            </p>
            
            <h3>4.3 Yasal Gereklilikler</h3>
            <p>
              Kişisel verilerinizi aşağıdaki durumlarda paylaşmamız gerekebilir:
            </p>
            <ul>
              <li>Yasal bir yükümlülüğe uymak için (mahkeme kararı veya yasal süreç dahil)</li>
              <li>Dersmaster'in haklarını veya mülkiyetini korumak için</li>
              <li>Acil durumlarda kişisel güvenlik tehditlerini önlemek için</li>
              <li>Dolandırıcılık ve diğer yasadışı faaliyetlere karşı koruma sağlamak için</li>
            </ul>
            
            <div className="bg-amber-50 dark:bg-amber-900/20 border-l-4 border-amber-400 dark:border-amber-600 p-4 mt-4">
              <p className="text-sm text-amber-700 dark:text-amber-300">
                <strong>Bilgi:</strong> Kişisel verilerinizi, açık rızanız olmadan reklam ve pazarlama amaçlı üçüncü taraflarla paylaşmıyoruz. Verileriniz, yalnızca Platform hizmetlerinin sağlanması için gerekli olan durumlarda ve yasal zorunluluklar kapsamında paylaşılmaktadır.
              </p>
            </div>
          </motion.section>
          
          {/* Veri Güvenliği */}
          <motion.section 
            id="security" 
            variants={fadeIn}
            className="prose prose-lg max-w-none dark:prose-invert prose-headings:text-gray-800 dark:prose-headings:text-white prose-p:text-gray-600 dark:prose-p:text-gray-300"
          >
            <h2 className="flex items-center">
              <ShieldCheckIcon className="h-8 w-8 text-indigo-600 dark:text-indigo-500 mr-3" />
              5. Veri Güvenliği
            </h2>
            
            <p>
              Dersmaster olarak, kişisel verilerinizin güvenliğini sağlamak için endüstri standardı güvenlik önlemlerini uyguluyoruz. Bu önlemler şunları içerir:
            </p>
            
            <ul>
              <li>Veri şifreleme (SSL/TLS teknolojisi)</li>
              <li>Güvenli erişim kontrolleri</li>
              <li>Düzenli güvenlik denetimleri</li>
              <li>İzinsiz erişim, değişiklik, açıklama veya veri imhasına karşı fiziksel, elektronik ve yönetimsel prosedürler</li>
              <li>Çalışanlara düzenli veri güvenliği eğitimi</li>
            </ul>
            
            <p>
              Ancak hiçbir elektronik iletim yöntemi veya internet veri depolama hizmeti %100 güvenli değildir. Verilerinizi korumak için makul önlemleri alırken, Platform üzerinden iletilen veya üzerinde saklanan bilgilerin mutlak güvenliğini garanti edemeyiz.
            </p>
            
            <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-400 dark:border-blue-600 p-4 mt-4">
              <p className="text-sm text-blue-700 dark:text-blue-300">
                <strong>Güvenlik İhlalleri:</strong> Kişisel verilerinizi etkileyen bir güvenlik ihlali durumunda, gereken en kısa süre içerisinde sizi bilgilendireceğiz ve kanunlarda öngörüldüğü şekilde ilgili düzenleyici otoritelere bildirimde bulunacağız.
              </p>
            </div>
          </motion.section>
          
          {/* Kullanıcı Hakları */}
          <motion.section 
            id="user-rights" 
            variants={fadeIn}
            className="prose prose-lg max-w-none dark:prose-invert prose-headings:text-gray-800 dark:prose-headings:text-white prose-p:text-gray-600 dark:prose-p:text-gray-300"
          >
            <h2 className="flex items-center">
              <LockClosedIcon className="h-8 w-8 text-indigo-600 dark:text-indigo-500 mr-3" />
              6. Kullanıcı Hakları
            </h2>
            
            <p>
              KVKK kapsamında aşağıdaki haklarınız bulunmaktadır:
            </p>
            
            <ol>
              <li>Kişisel verilerinizin işlenip işlenmediğini öğrenme</li>
              <li>Kişisel verileriniz işlenmişse buna ilişkin bilgi talep etme</li>
              <li>Kişisel verilerinizin işlenme amacını ve bunların amacına uygun kullanılıp kullanılmadığını öğrenme</li>
              <li>Yurt içinde veya yurt dışında kişisel verilerinizin aktarıldığı üçüncü kişileri bilme</li>
              <li>Kişisel verilerinizin eksik veya yanlış işlenmiş olması hâlinde bunların düzeltilmesini isteme</li>
              <li>KVKK'nın 7. maddesinde öngörülen şartlar çerçevesinde kişisel verilerinizin silinmesini veya yok edilmesini isteme</li>
              <li>Aktarılan üçüncü kişilere düzeltme, silme veya yok etme bildiriminin yapılmasını isteme</li>
              <li>İşlenen verilerinizin münhasıran otomatik sistemler vasıtasıyla analiz edilmesi suretiyle aleyhinize bir sonucun ortaya çıkmasına itiraz etme</li>
              <li>Kişisel verilerinizin kanuna aykırı olarak işlenmesi sebebiyle zarara uğramanız hâlinde zararın giderilmesini talep etme</li>
            </ol>
            
            <p>
              Bu haklarınızı kullanmak için <a href="mailto:info@dersmaster.com">info@dersmaster.com</a> adresine e-posta göndererek veya hesap ayarları bölümünden talepte bulunabilirsiniz.
            </p>
            
            <div className="bg-teal-50 dark:bg-teal-900/20 border-l-4 border-teal-400 dark:border-teal-600 p-4 mt-4">
              <p className="text-sm text-teal-700 dark:text-teal-300">
                <strong>Talebinize İlişkin Süreç:</strong> Talepleriniz, talebin niteliğine göre en kısa sürede ve en geç otuz (30) gün içinde ücretsiz olarak sonuçlandırılır. İşlemin ayrıca bir maliyet gerektirmesi hâlinde, Kişisel Verileri Koruma Kurumu tarafından belirlenen tarifedeki ücret alınabilir.
              </p>
            </div>
          </motion.section>
          
          {/* Çerezler ve İzleme */}
          <motion.section 
            id="cookies" 
            variants={fadeIn}
            className="prose prose-lg max-w-none dark:prose-invert prose-headings:text-gray-800 dark:prose-headings:text-white prose-p:text-gray-600 dark:prose-p:text-gray-300"
          >
            <h2 className="flex items-center">
              <FingerPrintIcon className="h-8 w-8 text-indigo-600 dark:text-indigo-500 mr-3" />
              7. Çerezler ve İzleme Teknolojileri
            </h2>
            
            <p>
              Platform'u ziyaret ettiğinizde, çerezler ve benzer teknolojiler kullanarak bilgileri otomatik olarak toplarız. Bu teknolojiler, kullanıcı deneyiminizi iyileştirmek ve Platform'un kullanımını analiz etmek için kullanılır.
            </p>
            
            <h3>7.1 Çerez Türleri</h3>
            <p>Platformumuzda kullanılan çerez türleri şunlardır:</p>
            <ul>
              <li><strong>Zorunlu Çerezler:</strong> Platform'un düzgün çalışması için gereklidir.</li>
              <li><strong>Performans Çerezleri:</strong> Kullanıcıların Platform'u nasıl kullandığını anlamamıza yardımcı olur.</li>
              <li><strong>İşlevsellik Çerezleri:</strong> Platform'da gerçekleştirdiğiniz seçimleri (kullanıcı adınız, dil seçiminiz veya bulunduğunuz bölge gibi) kaydeder.</li>
              <li><strong>Hedefleme/Reklam Çerezleri:</strong> İlgi alanlarınıza ve tercihlerinize göre kişiselleştirilmiş içerik sunmak için kullanılır.</li>
            </ul>
            
            <h3>7.2 Çerez Kontrolü</h3>
            <p>
              Çoğu web tarayıcısı, çerezleri otomatik olarak kabul eder, ancak isterseniz tarayıcı ayarlarınızı değiştirerek çerezleri reddetmeyi veya tarayıcınızın bir çerez gönderildiğinde sizi uyarmasını sağlayabilirsiniz. Çerezleri devre dışı bırakmak, Platform'un bazı özelliklerini kullanamamanıza neden olabilir.
            </p>
            
            <div className="bg-fuchsia-50 dark:bg-fuchsia-900/20 border-l-4 border-fuchsia-400 dark:border-fuchsia-600 p-4">
              <p className="text-sm text-fuchsia-700 dark:text-fuchsia-300">
                <strong>Üçüncü Taraf İzleme:</strong> Bazı üçüncü taraf hizmet sağlayıcılar (örneğin, Google Analytics) Platform'daki kullanıcı davranışlarını analiz etmek için çerezler ve benzer teknolojiler kullanabilir. Bu üçüncü tarafların bilgi toplama ve kullanma uygulamaları, onların kendi gizlilik politikalarına tabidir.
              </p>
            </div>
          </motion.section>
          
          {/* Politika Güncellemeleri */}
          <motion.section 
            id="updates" 
            variants={fadeIn}
            className="prose prose-lg max-w-none dark:prose-invert prose-headings:text-gray-800 dark:prose-headings:text-white prose-p:text-gray-600 dark:prose-p:text-gray-300"
          >
            <h2 className="flex items-center">
              <CogIcon className="h-8 w-8 text-indigo-600 dark:text-indigo-500 mr-3" />
              8. Politika Güncellemeleri
            </h2>
            
            <p>
              Bu Gizlilik Politikası'nı zaman zaman güncelleyebiliriz. Önemli değişiklikler olması durumunda, değişiklikler yürürlüğe girmeden önce Platform üzerinden bildirim yaparak veya kayıtlı e-posta adresinize bildirim göndererek sizi bilgilendireceğiz.
            </p>
            
            <p>
              Her güncellemeden sonra bu sayfanın üst kısmında "Son Güncelleme" tarihi değiştirilecektir. Önemli değişikliklerden sonra Platform'u kullanmaya devam etmeniz, güncellenen Gizlilik Politikası'nı kabul ettiğiniz anlamına gelecektir.
            </p>
            
            <p>
              Bu politikanın önceki sürümlerine erişmek için lütfen bizimle iletişime geçin.
            </p>
            
            <div className="bg-gray-50 dark:bg-gray-700/50 border-l-4 border-gray-400 dark:border-gray-500 p-4">
              <p className="text-sm text-gray-700 dark:text-gray-300">
                <strong>Değişiklikleri Takip Etme:</strong> Gizlilik Politikamızda yapılan değişiklikleri düzenli olarak kontrol etmenizi öneririz. Önemli güncellemeler hakkında bilgilendirilmek için e-posta bildirimlerini açık tutabilirsiniz.
              </p>
            </div>
          </motion.section>
          
          {/* İletişim */}
          <motion.section 
            id="contact" 
            variants={fadeIn}
            className="prose prose-lg max-w-none dark:prose-invert prose-headings:text-gray-800 dark:prose-headings:text-white prose-p:text-gray-600 dark:prose-p:text-gray-300"
          >
            <h2 className="flex items-center">
              <EnvelopeIcon className="h-8 w-8 text-indigo-600 dark:text-indigo-500 mr-3" />
              9. İletişim
            </h2>
            
            <p>
              Bu Gizlilik Politikası veya kişisel verilerinizin kullanımına ilişkin sorularınız, endişeleriniz veya talepleriniz varsa, lütfen bizimle aşağıdaki kanallardan iletişime geçin:
            </p>
            
            <div className="grid md:grid-cols-2 gap-6 not-prose mt-6">
              <div className="bg-white dark:bg-gray-800 shadow-md rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">E-posta İletişim</h3>
                <p className="text-gray-600 dark:text-gray-300 mb-2">Gizlilik ve Veri Koruma:</p>
                <a href="mailto:info@dersmaster.com" className="text-indigo-600 dark:text-indigo-400 font-medium hover:underline">info@dersmaster.com</a>
                
                <p className="text-gray-600 dark:text-gray-300 mt-4 mb-2">Veri İhlal Bildirimleri:</p>
                <a href="mailto:info@dersmaster.com" className="text-indigo-600 dark:text-indigo-400 font-medium hover:underline">info@dersmaster.com</a>
              </div>
              
              
            </div>
            
            <div className="mt-8 p-6 bg-indigo-50 dark:bg-indigo-900/20 rounded-xl not-prose">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Yasal Çerçeve</h3>
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                Bu Gizlilik Politikası, 6698 sayılı Kişisel Verilerin Korunması Kanunu (KVKK) ve ilgili diğer Türkiye Cumhuriyeti kanunlarına uygun olarak hazırlanmıştır. Kişisel verileriniz, bu yasal çerçeve kapsamında işlenmekte ve korunmaktadır.
              </p>
              <p className="text-gray-600 dark:text-gray-300">
                Kişisel verilerin korunması hakkındaki mevzuata ilişkin daha fazla bilgi için <a href="https://www.kvkk.gov.tr/" target="_blank" rel="noopener noreferrer" className="text-indigo-600 dark:text-indigo-400 hover:underline">Kişisel Verileri Koruma Kurumu</a> web sitesini ziyaret edebilirsiniz.
              </p>
            </div>
          </motion.section>
        </motion.div>
      </div>
    </div>
  );
};

export default Privacy;
